import JSMpeg from '@cycjimmy/jsmpeg-player';
import { VideoRenderer, VideoRendererHost } from './video-renderer';

// Legacy live path: ffmpeg transcodes RTSP to MPEG-1 in the relay and jsmpeg
// decodes it in WASM. Kept until Live:Transport is webrtc everywhere.
export class Mpeg1Renderer implements VideoRenderer {
  private player: JSMpeg.VideoElement | null = null;
  private lastFrame: number = 0;
  private started: boolean = false;
  private readonly session = new AbortController();

  constructor(
    private readonly wrapper: HTMLDivElement,
    private readonly host: VideoRendererHost,
    private readonly wsUrl: string,
    private readonly videoBufferSize?: number
  ) {}

  public start(): void {
    try {
      this.player = new JSMpeg.VideoElement(
        this.wrapper,
        this.wsUrl,
        {
          autoplay: true,
          autoSetWrapperSize: true,
          decodeFirstFrame: true,
          progressive: false,
          control: false,
          loop: false
        },
        {
          videoBufferSize: this.videoBufferSize,
          audio: false,
          disableGl: false,
          disableWebAssembly: false,
          preserveDrawingBuffer: false,
          // The only reliable liveness signal: load/play fire on the first
          // websocket byte even when ffmpeg dies right after (bad credentials).
          onVideoDecode: (): void => this.onFrame()
        }
      );

      this.lastFrame = Date.now();
      this.hookSocket();
    } catch (error: unknown) {
      this.host.onFatal(error instanceof Error ? error.message : 'jsmpeg failed to start');
    }
  }

  private onFrame(): void {
    this.lastFrame = Date.now();
    if (!this.started) {
      this.started = true;
      this.host.onStarted();
    }
  }

  // jsmpeg builds its websocket asynchronously and exposes no event for it.
  private hookSocket(): void {
    let attempts: number = 0;
    const maxAttempts: number = 10;

    const tryHook = (): void => {
      if (this.session.signal.aborted) {
        return;
      }

      const socket: WebSocket | undefined =
        (this.player as unknown as { player?: { source?: { socket?: WebSocket } } })?.player?.source?.socket;

      if (!socket) {
        if (++attempts < maxAttempts) {
          setTimeout(tryHook, 50);
        }
        return;
      }

      const onSocketDown = (): void => {
        if (this.session.signal.aborted || this.started) {
          return;
        }
        this.host.onFatal('relay socket closed');
      };

      socket.addEventListener('error', onSocketDown, { signal: this.session.signal });
      socket.addEventListener('close', onSocketDown, { signal: this.session.signal });
    };

    setTimeout(tryHook, 0);
  }

  public play(): void {
    this.lastFrame = Date.now();
    try { this.player?.play(); } catch { /* player already gone */ }
  }

  public pause(): void {
    try { this.player?.pause(); } catch { /* player already gone */ }
  }

  public lastFrameAt(): number {
    return this.lastFrame;
  }

  public destroy(): void {
    // Abort first: destroy() raises a socket close we must not read as failure.
    this.session.abort();

    if (this.player) {
      try { this.player.destroy(); } catch { /* already destroyed */ }
      this.player = null;
    }

    // jsmpeg leaves its canvas behind: a stale one sits under the next renderer
    // and every rebuild leaks a WebGL context.
    this.wrapper.replaceChildren();
  }
}
